import * as React from 'react';

/** Matches ProgramCurriculumCollections `referenceCollectionId` union (preset vs org reference). */
export type ReferenceCollectionIdMember =
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_presetCollectionIdMember';
      presetCollectionId?: { id?: string | null } | null;
    }
  | {
      __typename?: 'ProgramCurriculumCollectionsV1_orgCollectionIdMember';
      orgCollectionId?: { id?: string | null } | null;
    };

export type ReferenceCollectionConfig = {
  enableSync?: boolean | null;
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/** Shape needed to read a preset reference id (shared by context + collection fragments). */
export type ReferenceCollectionConfigurationLike = {
  referenceCollectionId?: ReferenceCollectionIdMember | null;
} | null;

/**
 * Reference library id for synced curriculum tracks: preset library or org collection.
 * Used the same way for tracking (e.g. isPartOfReorderedCollection) regardless of reference type.
 */
export const getSyncedReferenceCollectionId = (
  config: ReferenceCollectionConfigurationLike | undefined
): string | undefined => {
  const ref = config?.referenceCollectionId;
  if (!ref) return undefined;
  if ('presetCollectionId' in ref && ref.presetCollectionId?.id) {
    return ref.presetCollectionId.id;
  }
  if ('orgCollectionId' in ref && ref.orgCollectionId?.id) {
    return ref.orgCollectionId.id;
  }
  return undefined;
};

/**
 * Config with server-confirmed existence of the backing collection.
 * referenceCollectionExists: null = query in flight, true = exists, false = NOT_FOUND/error/no sync.
 */
export type ResolvedReferenceCollectionConfig = {
  enableSync?: boolean | null;
  referenceCollectionId?: ReferenceCollectionIdMember | null;
  referenceCollectionExists: boolean | null;
};

type ReferenceCollectionConfigContextType = {
  /** Map of collection ID to resolved reference collection configuration. */
  referenceCollectionConfigById: Record<string, ResolvedReferenceCollectionConfig>;
};

const ReferenceCollectionConfigContext = React.createContext<ReferenceCollectionConfigContextType>({
  referenceCollectionConfigById: {},
});

export const useReferenceCollectionConfig = () => React.useContext(ReferenceCollectionConfigContext);

/**
 * Returns true only when the collection is synced to a reference library (preset or org),
 * enableSync is true, AND the backing collection is confirmed to exist server-side.
 * Returns false while loading (existence query in flight) or on NOT_FOUND/error.
 */
export const useIsPartOfReorderedCollection = (collectionId: string | undefined): boolean => {
  const { referenceCollectionConfigById } = useReferenceCollectionConfig();
  if (!collectionId) return false;
  const config = referenceCollectionConfigById[collectionId];
  if (!config) return false;
  return (
    config.enableSync === true &&
    Boolean(getSyncedReferenceCollectionId(config)) &&
    config.referenceCollectionExists === true
  );
};

export default ReferenceCollectionConfigContext;
